<script setup>
/**
 * One session, collapsed to a row and expanding in place.
 *
 * The card never opens itself: `open` is a prop and a click only asks. The
 * list above owns which one is open, which is what makes "one at a time" a
 * consequence of there being a single value rather than a rule each card has
 * to remember.
 *
 * Three actions, the same three the calendar's board offers: Open the room
 * (on the row itself — "Back to the room" while it is running), Edit and
 * Delete — one word for it, as on the calendar (Cancel means "leave it alone"). Whether a repeating session's edit is for this date or the whole
 * series, and skipping a date, are decided inside Edit, not by a row of
 * buttons out here. Edit and Delete are for those who may change the agenda;
 * anyone on the staff side can open the room.
 *
 * Q&A, polls and breakout rooms are on the design board and nowhere in this
 * platform, so the panel lists the two settings that exist rather than showing
 * controls no form can change. Everything else the board draws — the audience
 * chip, the repeat chip, the series' remaining dates — is here, because the
 * backend grew an answer for each.
 */
import { computed } from 'vue';
import IconChevronRight from '../icons/IconChevronRight.vue';
import IconRepeat from '../icons/IconRepeat.vue';
import IconVideo from '../icons/IconVideo.vue';
import XlButton from '../xl/XlButton.vue';
import XlCard from '../xl/XlCard.vue';
import XlTag from '../xl/XlTag.vue';
import XlTooltip from '../xl/XlTooltip.vue';
import {
  datesLeftLabel,
  windowLabel,
  cardTimeLabel,
  dateChip,
  hasEnded,
  parseSessionDate,
  isGuestSpeaker,
  metaLine,
  recordingLabel,
  repeatLabel,
  resourcesLabel,
  shortDate,
  speakerName,
  DELETE_REFUSED,
} from '../../helpers/sessionCard';
import { SESSION_KINDS, kindOf } from '../../helpers/sessionKinds';
import { bookFor, describeAccess, ownBookOf } from '../../composables/useResourceAccess';
import useAudienceNames from '../../composables/useAudienceNames';
import AddedBy from '../resource-access/AddedBy.vue';
import { useAuthStore } from '../../stores/auth';
import { roomLabel } from '../../helpers/roomVisits';

const props = defineProps({
  session: { type: Object, required: true },
  open: { type: Boolean, default: false },
  /** Named segments, so an audience of one reads as its name, not "1 segment". */
  segments: { type: Array, default: () => [] },
  /** Whether this viewer may edit and cancel — the API's rule, so no button here 403s. */
  canManage: { type: Boolean, default: true },
  canEdit: { type: Boolean, default: false },
  /** Ticks from the list, so "Starts in 8 min" counts down and the way in turns into a way back. */
  now: { type: Date, default: () => new Date() },
});

const emit = defineEmits(['toggle', 'join', 'edit', 'cancel']);

const chip = computed(() => dateChip(props.session));
const meta = computed(() => metaLine(props.session));
const isDraft = computed(() => props.session.status === 'draft');
const isCancelled = computed(() => props.session.status === 'cancelled');
const ended = computed(() => hasEnded(props.session, props.now));
const repeats = computed(() => repeatLabel(props.session));

// The way in. A cancelled or finished session has nothing left to open.
const startsAt = computed(() => parseSessionDate(props.session.start_date));
const endsAt = computed(() => parseSessionDate(props.session.end_date));
const isLive = computed(() => Boolean(startsAt.value && endsAt.value && props.now >= startsAt.value && props.now < endsAt.value));
// "Back to the room" only for somebody who has been in it (roomVisits).
const joinLabel = computed(() => roomLabel(props.session.id, isLive.value));
const canOpen = computed(() => !isCancelled.value && !ended.value);
const SOON_MINUTES = 60;
const minutesToStart = computed(() => (startsAt.value ? Math.ceil((startsAt.value - props.now) / 60000) : null));
/** "Live now", or "Starts in 8 min" for the one about to begin — so the row you want stands out. */
const roomStatus = computed(() => {
  if (!canOpen.value) return '';
  if (isLive.value) return 'Live now';
  const minutes = minutesToStart.value;

  return minutes !== null && minutes > 0 && minutes <= SOON_MINUTES ? `Starts in ${minutes} min` : '';
});

// Office Hours is a drop-in: a desk somebody is on for a window, not a
// session of fixed length that people book.
const isDropIn = computed(() => kindOf(props.session) === SESSION_KINDS.OFFICE_HOURS);
const timeLine = computed(() => (isDropIn.value ? windowLabel(props.session) : cardTimeLabel(props.session)));

// Every date of the series, so it is clear what was scheduled: the ones
// that have been, the ones called off, the next one, and the rest. An older
// payload carries only the dates still to come.
const DATE_STATE = {
  next: { tone: 'brand', note: ' · next' },
  cancelled: { tone: 'error', note: ' · cancelled' },
  done: { tone: '', note: '' },
  upcoming: { tone: '', note: '' },
};
const dates = computed(() => {
  const schedule = props.session.series_schedule
    ?? (props.session.series_dates ?? []).map((start_date, index) => ({ start_date, state: index === 0 ? 'next' : 'upcoming' }));

  return schedule
    .map((date) => ({ ...date, label: shortDate(date.start_date), ...(DATE_STATE[date.state] ?? DATE_STATE.upcoming) }))
    .filter((date) => date.label);
});

// Who may attend. The same reading the Policyholders and Resources lists use,
// so one audience is never described two ways: everyone reads green, a
// narrowed set amber, nobody red.
// A broker's session reads "My policyholders" to them and "Maya Lewis's
// policyholders" to everybody else; the agency's, "Everyone in the portal".
const auth = useAuthStore();
const { members: hubMembers, segments: hubSegments } = useAudienceNames(() => props.session.audience);
const audience = computed(() => describeAccess(props.session.audience, props.segments.length ? props.segments : hubSegments.value, {
  book: bookFor(props.session, auth.userData),
  members: hubMembers.value,
  me: ownBookOf(auth.userData),
}));

const host = computed(() => speakerName(props.session.moderator ?? props.session.session_moderator) ?? '—');
const speakers = computed(() => (props.session.session_speakers ?? [])
  .map((speaker) => ({ name: speakerName(speaker), guest: isGuestSpeaker(speaker) }))
  .filter((speaker) => speaker.name));

// Only the two settings that exist. Q&A, polls and breakout rooms are on the
// board but nowhere in this platform — listing them as a permanent "Off" would
// describe a control no form can change.
const settings = computed(() => [
  ['In-session chat', props.session.in_session_chat_enabled ? 'On' : 'Off'],
  ['Recording', recordingLabel(props.session)],
]);
</script>

<template>
  <XlCard
    class="session-card"
    :class="{ 'session-card--open': props.open }"
    :data-testid="`session-card-${props.session.id}`"
  >
    <div
      class="session-card__row"
      role="button"
      tabindex="0"
      :aria-expanded="props.open ? 'true' : 'false'"
      :data-testid="`session-toggle-${props.session.id}`"
      @click="emit('toggle', props.session)"
      @keydown.enter="emit('toggle', props.session)"
      @keydown.space.prevent="emit('toggle', props.session)"
    >
      <span class="session-card__date" aria-hidden="true">
        <span class="session-card__weekday">{{ chip.weekday }}</span>
        <span class="session-card__day">{{ chip.day }}</span>
        <span class="session-card__month">{{ chip.month }}</span>
      </span>

      <span class="session-card__main">
        <span class="session-card__heading">
          <b class="session-card__title">{{ props.session.title }}</b>
          <XlTag v-if="isDropIn" tone="success" :data-testid="`session-dropin-${props.session.id}`">Drop-in</XlTag>
          <XlTag
            v-if="repeats"
            tone="brand"
            :data-testid="`session-repeats-chip-${props.session.id}`"
          ><IconRepeat color="currentColor" size="12" />{{ repeats }}</XlTag>
          <XlTag v-if="isDraft" tone="warning" :data-testid="`session-draft-${props.session.id}`">Draft</XlTag>
          <XlTag v-if="isCancelled" tone="error" :data-testid="`session-cancelled-${props.session.id}`">Cancelled</XlTag>
          <!-- The one thing today's table knows that the board never had to
               model: the same person cannot host two sessions at once. -->
          <XlTag
            v-if="props.session.isOverlapping"
            tone="error"
            :data-testid="`session-overlap-${props.session.id}`"
          >Host is double-booked</XlTag>
        </span>

        <span class="session-card__time" :data-testid="`session-time-${props.session.id}`">
          {{ timeLine }}
        </span>
        <span class="session-card__meta" :data-testid="`session-meta-${props.session.id}`"><AddedBy :created-by="props.session.created_by" :name="props.session.creator_name" />{{ meta }}</span>
      </span>

      <!-- Who it reaches, so a scan down the list shows which sessions are
           narrow without opening any of them — and the way in, on every row:
           staff open a room to set it up, look in, or test it before anyone
           arrives. -->
      <span class="session-card__audience">
        <XlTag :tone="audience.tone" :data-testid="`session-audience-${props.session.id}`">{{ audience.label }}</XlTag>
        <span
          v-if="roomStatus"
          class="session-card__status"
          :class="{ 'session-card__status--live': isLive }"
          :data-testid="`session-status-${props.session.id}`"
        >{{ roomStatus }}</span>
        <XlButton
          v-if="canOpen"
          :variant="isLive ? 'danger' : roomStatus ? 'success' : 'ghost'"
          size="sm"
          :data-testid="`session-join-${props.session.id}`"
          @click.stop="emit('join', props.session)"
          @keydown.enter.stop
          @keydown.space.stop
        ><IconVideo color="currentColor" size="14" />{{ joinLabel }}</XlButton>
      </span>

      <span class="session-card__chevron" :class="{ 'session-card__chevron--open': props.open }" aria-hidden="true">
        <IconChevronRight color="currentColor" />
      </span>
    </div>

    <div v-if="props.open" class="session-card__panel" :data-testid="`session-panel-${props.session.id}`">
      <div class="session-card__columns">
        <div>
          <b class="session-card__column-title">Details</b>

          <!-- "Host", not "Admin": the person who runs it, in the word the
               host/speaker split made correct. -->
          <div class="session-card__detail">
            <span class="session-card__label">Host</span>
            <span class="session-card__value" :data-testid="`session-host-${props.session.id}`">{{ host }}</span>
          </div>

          <div class="session-card__detail">
            <span class="session-card__label">Speakers</span>
            <span class="session-card__value" :data-testid="`session-speakers-${props.session.id}`">
              <template v-if="speakers.length">
                <span v-for="(speaker, index) in speakers" :key="speaker.name" class="session-card__speaker">
                  {{ speaker.name }}<XlTag v-if="speaker.guest" class="session-card__guest">guest</XlTag
                  ><template v-if="index < speakers.length - 1">, </template>
                </span>
              </template>
              <template v-else>—</template>
            </span>
          </div>

          <div v-for="[label, value] in settings" :key="label" class="session-card__detail">
            <span class="session-card__label">{{ label }}</span>
            <span class="session-card__value" :data-testid="`session-setting-${label}`">{{ value }}</span>
          </div>

          <div class="session-card__detail">
            <span class="session-card__label">Resources</span>
            <span class="session-card__value" :data-testid="`session-resources-${props.session.id}`">
              {{ resourcesLabel(props.session, audience.label) }}
            </span>
          </div>
        </div>

        <!-- Every date, not a sentence about them: a reader looking at a
             fortnightly series wants to see which Tuesdays it actually lands
             on, including the one it skips. -->
        <div v-if="dates.length">
          <b class="session-card__column-title">Every date in this series</b>
          <div class="session-card__dates" :data-testid="`session-dates-${props.session.id}`">
            <XlTag
              v-for="date in dates"
              :key="`${date.start_date}-${date.state}`"
              :tone="date.tone"
              :class="`session-card__date-chip--${date.state}`"
              :data-state="date.state"
            >{{ date.label }}{{ date.note }}</XlTag>
          </div>
        </div>

        <div v-else>
          <b class="session-card__column-title">Repeats</b>
          <p class="session-card__repeats" :data-testid="`session-repeats-${props.session.id}`">
            <template v-if="repeats">
              {{ repeats }} · {{ datesLeftLabel(props.session.series_remaining) }}
            </template>
            <!-- The board's own words for a session that does not repeat. -->
            <template v-else>This is a one-off. Turn it into a series by editing it.</template>
          </p>
        </div>
      </div>

      <div v-if="props.canManage || props.canEdit" class="session-card__actions">
        <!-- One Edit: for a repeating session, this date or the whole series
             is chosen in the edit screen, and so is skipping a date. -->
        <XlButton
          v-if="!isCancelled"
          variant="ghost"
          size="sm"
          :data-testid="`session-edit-${props.session.id}`"
          @click.stop="emit('edit', props.session)"
        >Edit</XlButton>

        <span class="session-card__spacer" />

        <XlButton
          v-if="props.canManage"
          variant="ghost-danger"
          size="sm"
          :data-testid="`session-cancel-${props.session.id}`"
          @click.stop="emit('cancel', props.session)"
        >Delete</XlButton>
        <!-- Offered Edit on an agency session, a broker may still not delete it. -->
        <XlTooltip v-else :text="DELETE_REFUSED" control>
          <XlButton
            variant="ghost-danger"
            size="sm"
            unavailable
            :data-testid="`session-cancel-${props.session.id}`"
            @click.stop
          >Delete</XlButton>
        </XlTooltip>
      </div>
    </div>
  </XlCard>
</template>

<style scoped>
.session-card {
  margin-bottom: 12px;
  padding: 0;
  overflow: hidden;
}

/* The open card is outlined, which is what tells a reader at a glance which
   row the panel below belongs to. */
.session-card--open {
  border-color: var(--xl-color-primary);
}

.session-card__row {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 18px 20px;
  cursor: pointer;
  font-family: var(--xl-font-family);
  transition: background-color 130ms ease;
}

.session-card__row:hover {
  background: var(--xl-color-primary-container);
}

@media (prefers-reduced-motion: reduce) {
  .session-card__row { transition: none; }
}

.session-card__row:focus-visible {
  outline: 2px solid var(--xl-color-primary);
  outline-offset: -2px;
}

.session-card__date {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 0 0 58px;
  width: 58px;
  height: 64px;
  border-radius: 14px;
  background: var(--xl-color-primary-container);
  color: var(--xl-color-primary);
  line-height: 1.1;
}

.session-card__weekday,
.session-card__month {
  font-size: 10.5px;
  text-transform: uppercase;
  opacity: 0.75;
}

/* The weekday carries the chip and the month trails it, so they are not the
   same weight on the board. */
.session-card__weekday { font-weight: 700; letter-spacing: 0.08em; }
.session-card__month { font-weight: 600; letter-spacing: 0.06em; }

.session-card__day {
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}

.session-card__main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.session-card__heading {
  display: flex;
  align-items: center;
  gap: 9px;
  flex-wrap: wrap;
}

.session-card__title {
  font-size: 16.5px;
  letter-spacing: -0.01em;
  color: var(--xl-color-on-surface);
}

.session-card__time {
  margin-top: 5px;
  font-size: 14px;
  font-weight: 500;
  color: var(--xl-color-on-surface-variant);
}

.session-card__meta {
  margin-top: 3px;
  font-size: 13px;
  color: var(--xl-color-outline);
}

.session-card__audience {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex: 0 0 auto;
  text-align: right;
}

/* Prototype `.sesnow`: the imminent row's status above its way in, and
   "Live now" in red behind a dot. */
.session-card__status {
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  white-space: nowrap;
  color: var(--xl-color-primary);
}

.session-card__status--live {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--xl-color-error);
}

.session-card__status--live::before {
  content: '';
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--xl-color-error);
}

.session-card__chevron {
  display: flex;
  flex: 0 0 auto;
  color: var(--xl-color-outline);
  transition: transform 150ms ease;
}

.session-card__chevron--open {
  transform: rotate(90deg);
}

@media (prefers-reduced-motion: reduce) {
  .session-card__chevron { transition: none; }
}

.session-card__panel {
  border-top: 1px solid var(--xl-color-border);
  background: var(--xl-color-background);
  padding: 16px 20px;
  font-family: var(--xl-font-family);
}

.session-card__columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  align-items: start;
}

@media (max-width: 720px) {
  .session-card__columns { grid-template-columns: 1fr; }
}

.session-card__column-title {
  font-size: 13px;
  color: var(--xl-color-on-surface);
}

.session-card__detail {
  display: flex;
  gap: 10px;
  padding: 6px 0;
  font-size: 13.5px;
}

.session-card__label {
  flex: 0 0 120px;
  width: 120px;
  color: var(--xl-color-outline);
}

.session-card__value {
  font-weight: 500;
  color: var(--xl-color-on-surface);
}

.session-card__speaker { white-space: nowrap; }

.session-card__guest { margin-left: 6px; }

.session-card__dates {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 9px;
}

/* A date that has been is still part of what was scheduled, just quieter;
   a cancelled one is struck through so it cannot be read as on. */
.session-card__date-chip--done { opacity: 0.55; }
.session-card__date-chip--cancelled { text-decoration: line-through; }

.session-card__repeats {
  margin: 8px 0 0;
  font-size: 13.5px;
  color: var(--xl-color-outline);
}

.session-card__actions {
  display: flex;
  align-items: center;
  gap: 9px;
  flex-wrap: wrap;
  margin-top: 16px;
}

.session-card__spacer { flex: 1; }
</style>
